import React from 'react';
import {
  Box,
  Checkbox,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';

// eslint-disable-next-line import/no-cycle
import { ColumnObject } from './ZerpaTable';
import { getDateDE } from '../../../utilities/dateHelper';
import { SetZerpaSelectedIdsActions } from './useCheckbox';

export type RowProperty = unknown;

export type ZerpaTableBodyProps<T> = {
  tableRows?: T[];
  columns: ColumnObject[];
  noDataDefaultText?: string;
  keyIdentifier: string;
  rowClick?: (arg1: T) => void;
  renderOnSelected?: (arg1: RowProperty) => React.ReactNode | undefined;
  multiselect?: boolean;
  color?: 'green';
}

/**
 * Types not exported to ZerpaTable and
 * only handeled internally by ZerpaTableBody component
 */
type PrivateProps = {
  selectedRow?: RowProperty;
  // onRowClick is internally used i.e for setting selectedRow from parent
  onRowClick?: (arg1: RowProperty) => void;
  checkedRows?: RowProperty[];
  setCheckedRows?: React.Dispatch<React.SetStateAction<RowProperty[]>> | SetZerpaSelectedIdsActions;
};

type Props<T> = ZerpaTableBodyProps<T> & PrivateProps;

function ZerpaTableBody<T extends {[key:string]:unknown}>(props: Props<T>): JSX.Element {
  const {
    tableRows = [],
    noDataDefaultText = 'No Data Available',
    columns,
    keyIdentifier,
    onRowClick,
    rowClick,
    selectedRow,
    multiselect = true,
    renderOnSelected = undefined,
    color,
    setCheckedRows,
    checkedRows,
  } = props;
  const maxRating = 5;
  const colLength = columns.length;

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>, row: T): void => {
    const { checked } = e.target;
    const rowId: RowProperty = row[keyIdentifier];
    if (checked && setCheckedRows !== undefined && checkedRows !== undefined) {
      if (!multiselect) {
        setCheckedRows([rowId]);
      } else {
        setCheckedRows(checkedRows?.concat(rowId));
      }
    } else if (!checked && setCheckedRows !== undefined && checkedRows !== undefined) {
      setCheckedRows(checkedRows.filter((id) => row[keyIdentifier] !== id));
    }
  };

  const convertToRatings = (ratings: number): React.ReactNode => {
    const ratingStar = new Array(ratings).fill(1).map((e, index) => <Star key={`f-${e + index}`} />);
    const remainingStart = new Array(maxRating - ratings).fill(1).map((e, index) => <StarBorder key={`e-${e + index}`} />);
    return <Box sx={{ minWidth: 130 }}>{[...ratingStar, ...remainingStart]}</Box>;
  };

  const renderRows = (): React.ReactNode => tableRows.map((row) => {
    const active = selectedRow === row[keyIdentifier];
    const identifier = row[keyIdentifier];
    return (
      <React.Fragment key={`${identifier}`}>
        <TableRow
          className="zerpatable-row"
          sx={{ cursor: 'pointer' }}
          key={`row-${identifier}`}
          onClick={(e): void => {
            const returnArg = identifier;
            if (onRowClick) {
              onRowClick(returnArg);
            }
            if (rowClick) {
              rowClick.apply(e, [row]);
            }
          }}
        >
          {
            columns.map((column) => {
              const key = column.keyName;
              const format = column.format || { align: 'inherit' };
              if (column.type === 'checkbox') {
                return (
                  <TableCell align={format.align} key={`col-${identifier}-${key}`}>
                    <Checkbox
                      onChange={(e): void => { handleCheck(e, row); }}
                      checked={checkedRows && checkedRows.includes(row[keyIdentifier])}
                      color="secondary"
                    />
                  </TableCell>
                );
              }
              let result = row[key];
              if (column.isDate && !!result) {
                result = getDateDE(row[key] as string);
              }
              if (typeof result === 'boolean') {
                result = result ? 'true' : 'false';
              } else if (column.type === 'rating') {
                result = convertToRatings(Number(result));
              } else if (typeof result === 'number') {
                result = result.toString();
              } else if (typeof result === 'string') {
                result = result.trim();
              }
              if (!result) {
                result = '-';
              }
              return (<TableCell align={format.align} key={`col-${identifier}-${key}`}>{result as (string|number|React.ReactNode)}</TableCell>);
            })
          }
        </TableRow>
        {
          active
          && renderOnSelected
          && (
            <TableRow
              key={`row-extra-${identifier}`}
            >
              <TableCell colSpan={100} align="justify">
                {active && renderOnSelected && renderOnSelected(identifier)}
              </TableCell>
            </TableRow>
          )
        }
      </React.Fragment>
    );
  });

  const renderEmptyRow = (): React.ReactNode => (
    <TableRow>
      <TableCell colSpan={colLength} style={{ textAlign: 'center', margin: '1em' }}>
        {noDataDefaultText}
      </TableCell>
    </TableRow>
  );

  return (
    <TableBody
      sx={{
        '& .zerpatable-row:nth-of-type(odd)': {
          backgroundColor: color ? 'tertiary.lightshade' : 'primary.lightshade',
        },
      }}
    >
      {tableRows.length > 0 ? renderRows() : renderEmptyRow()}
    </TableBody>
  );
}

export default ZerpaTableBody;
